import { extendTheme, Theme } from "@mui/material/styles";

// Primitives: the brand values the roles below draw from. Components never read
// these directly, they read palette roles.
const primaryColor = "#00A0A1";
const secondaryColor = "#3c4565";
const white = "#ffffff";
const cream = "#f7f3e8";
const darkGreen = "#012625";
const mint = "#67D6A1";
const greenInk = "#178353";
const nancieViolet = "#8473EA";
const nancieGradient = "linear-gradient(90deg, #C580F1 0%, #8473EA 100%)";
const cyan = "#00CCCC";
const navy900 = "#1f2037";
const navy800 = "#25283F";
const navy700 = "#333b54";

export const themeOptions = extendTheme({
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: 'Lato, "Helvetica Neue", Arial, Helvetica, sans-serif',
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: "small",
      },
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.button.background,
            color: theme.vars.palette.button.text,
            "&:hover": {
              backgroundColor: theme.vars.palette.button.backgroundHover,
            },
          }),
        },
      ],
    },
    MuiButtonGroup: {
      defaultProps: {
        size: "small",
      },
    },
    MuiChip: {
      variants: [
        {
          // A clickable primary chip is a selected filter: same fill as the
          // primary action button. Non-clickable primary chips stay badges.
          props: { color: "primary", variant: "filled" },
          style: ({ theme }) => ({
            "&.MuiChip-clickable": {
              backgroundColor: theme.vars.palette.button.background,
              color: theme.vars.palette.button.text,
              "&:hover, &.Mui-focusVisible": {
                backgroundColor: theme.vars.palette.button.backgroundHover,
              },
            },
          }),
        },
        {
          props: { color: "secondary", variant: "filled" },
          style: ({ theme }) => ({
            backgroundColor: theme.vars.palette.button.chip,
            color: theme.vars.palette.button.chipText,
            "&.MuiChip-clickable:hover, &.Mui-focusVisible": {
              backgroundColor: theme.vars.palette.button.chipHover,
            },
          }),
        },
      ],
    },
    MuiCheckbox: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: "inherit",
          "&.Mui-checked": {
            color: theme.vars.palette.primary.main,
          },
        }),
      },
    },
    MuiFab: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormControl: {
      defaultProps: {
        margin: "dense",
        size: "small",
      },
    },
    MuiFormHelperText: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme }) => ({
          "&.MuiIconButton-root.Mui-disabled": {
            color: theme.vars.palette.text.disabled,
          },
        }),
      },
    },
    MuiInputBase: {
      defaultProps: {
        margin: "dense",
      },
    },
    // A field draws its outline once, on its root. MUI's notched fieldset is
    // off so labels above the field never cut the line; focus and error move
    // to the root border.
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.vars.palette.border.inputOutlined}`,
          "&.Mui-focused": { borderColor: theme.vars.palette.primary.main },
          "&.Mui-error": { borderColor: theme.vars.palette.error.main },
        }),
        notchedOutline: { border: 0 },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.vars.palette.border.inputOutlined}`,
          "&.Mui-focused": { borderColor: theme.vars.palette.primary.main },
          "&.Mui-error": { borderColor: theme.vars.palette.error.main },
        }),
        notchedOutline: { border: 0 },
      },
    },
    MuiPickersInputBase: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiInputLabel: {
      defaultProps: {
        margin: "dense",
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: ({ theme }) => ({
          backgroundColor: theme.vars.palette.LinearProgress.track,
          "& .MuiLinearProgress-bar": {
            backgroundColor: theme.vars.palette.primary.main,
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: "unset",
          boxShadow: theme.vars.palette.shadow.main,
          "&.tile-shadow": {
            boxShadow: "none",
            ...theme.applyStyles("light", {
              border: `1px solid ${theme.vars.palette.border.paper}`,
            }),
          },
        }),
        outlined: ({ theme }) => ({
          border: 0,
          ...theme.applyStyles("light", {
            border: `1px solid ${theme.vars.palette.border.paper}`,
          }),
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.dialog,
        }),
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          backgroundColor: theme.vars.palette.tooltip.background,
          color: theme.vars.palette.text.contrastPrimary,
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        size: "small",
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 0,
          // Header, footer and pinned edges read the MUI X row-border variable
          // or the withBorderColor class; rows draw their own border in
          // Table/styles.tsx. One colour for all grid lines.
          "--DataGrid-rowBorderColor": theme.vars.palette.background.default,
          "& .MuiDataGrid-withBorderColor": {
            borderColor: theme.vars.palette.background.default,
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: theme.vars.palette.DataGrid.headerBg,
          },
          "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-columnHeader--pinnedRight":
            {
              backgroundColor: theme.vars.palette.DataGrid.headerBg,
              backgroundImage: "unset",
              borderWidth: "1px",
            },
          "& .MuiDataGrid-columnHeaders .MuiDataGrid-scrollbarFiller--header.MuiDataGrid-scrollbarFiller--pinnedRight":
            {
              backgroundColor: theme.vars.palette.DataGrid.headerBg,
            },
          "& .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: theme.vars.palette.background.paper,
            backgroundImage: "unset",
            borderWidth: "1px",
          },
          "& .MuiDataGrid-columnHeaders > div, & .MuiDataGrid-pinnedColumnHeaders > div":
            {
              borderBottom: `1px solid ${theme.vars.palette.background.paper}`,
              backgroundColor: `${theme.vars.palette.DataGrid.headerBg} !important`,
              border: 0,
            },
          "& .MuiDataGrid-cell": { borderTop: 0 },
          "& .MuiDataGrid-columnHeaderTitle": {
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
          },
          "& .weekendDate, & .weekendDate .MuiDataGrid-cell--pinnedLeft, & .weekendDate .MuiDataGrid-cell--pinnedRight":
            {
              backgroundColor: theme.vars.palette.background.sunken,
            },
          "& .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler div": {
            backgroundColor: "transparent !important",
            pointerEvents: "none",
            border: "unset",
          },
          "& .MuiDataGrid-main .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-main .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-main .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-main .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight, & .MuiDataGrid-main .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-main .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight":
            {
              backgroundColor: "unset",
            },
        }),
        panelContent: ({ theme }) => ({
          backgroundColor: theme.vars.palette.background.paper,
        }),
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: ({ theme }) => ({
          backgroundColor: theme.vars.palette.primary.main,
        }),
      },
    },
    MuiTextField: {
      defaultProps: {
        margin: "dense",
        size: "small",
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        margin: "dense",
        size: "small",
      },
    },
  },
  colorSchemes: {
    light: {
      palette: {
        mode: "light",
        common: {
          white: white,
        },
        // Aliases kept for KPI format rules stored in tenant databases.
        customColors: {
          purple: {
            main: "#7942c1",
          },
          orange: {
            main: "#FF8D47",
          },
        },
        background: {
          default: cream,
          dialog: "#FFFFFF",
          input: "#ebebeb",
          login: mint,
          paper: "#FFFFFF",
          inverted: darkGreen,
          sunken: "#ebebeb",
        },
        border: {
          paper: "#DADADA",
          inputOutlined: "#DADADA",
          dotted: "#DADADA",
        },
        button: {
          background: mint,
          backgroundHover: `color-mix(in srgb, ${mint} 88%, black)`,
          text: darkGreen,
          tonalBackground: "#ebebeb",
          tonalBackgroundHover: "#DADADA",
          switch: "#ebebeb",
          chip: mint,
          chipHover: `color-mix(in srgb, ${mint} 88%, black)`,
          chipText: darkGreen,
        },
        divider: "#DADADA",
        error: {
          main: "#dc2935",
          dark: "#c9202c",
        },
        filter: {
          background: darkGreen,
          text: white,
          textSecondary: "rgba(255, 255, 255, 0.7)",
          textDisabled: "rgba(255, 255, 255, 0.38)",
          tabBackground: white,
          toggleBackground: "#ebebeb",
          toggleBackgroundHover: "#DADADA",
          toggleSelectedBackground: mint,
          toggleSelectedBackgroundHover: "rgb(103, 214, 161, 0.85)",
          toggleBorder: "#DADADA",
          toggleSelectedText: darkGreen,
        },
        chart: {
          positive: "#329c6d",
          negative: "#e22e3a",
          neutral: "#d0cec8",
          // Darker twin of `neutral`: charts that need two distinguishable greys
          // side by side (e.g. "flat price" vs "no price data").
          neutralDark: "#8f8a80",
          neutralStroke: "#8f8a80",
          previous: "#3d4a57",
          forecast: "#6d6a65",
          model: nancieViolet,
          pm: "#3f6aa8",
          pickup: "#df7300",
          cancellation: "#e8836c",
          reference: "#df7300",
          raf: "#d0cec8",
          corridor: "#f3f4f6",
          salesWash: "#cff6c6",
          pickupWash: "#d4ba98",
          axis: "#767676",
          grid: "rgba(1, 38, 37, 0.1)",
          highlight: "rgba(31, 132, 76, 0.15)",
          selection: "rgba(31, 132, 76, 0.25)",
          needle: "#d6d6d6",
          // Event bands under the charts: one violet identity, not the per-event
          // backend colour.
          event: "rgba(130, 80, 200, 0.85)",
          eventStroke: "rgba(90, 40, 160, 0.9)",
          eventText: "#ffffff",
          // Legacy cockpit gauge shades (--highlighted-decile, --decile-*).
          gauge: {
            target: "#35c580",
            empty: "#f2f2f2",
            edge: "#f7f2e8",
            deciles25: "#8fe1aa",
            decile6: "#8fe1aa",
            decile7: "#a2e9a9",
            decile8: "#cff6c7",
            decile9: "#e3fade",
          },
          series: {
            1: "#b14f9f",
            2: "#dc7d12",
            3: "#2b7ad6",
            4: "#b24b0a",
            5: "#068fa7",
            6: "#ac2f3b",
            7: "#515bc3",
            8: "#d06b9e",
            9: "#8459c3",
            10: "#536f02",
            11: "#046b96",
            12: "#b18e15",
          },
        },
        LinearProgress: {
          track: "#ebebeb",
        },
        primary: {
          main: greenInk,
          light: mint,
        },
        menu: {
          background: darkGreen,
          text: cream,
          hover: "rgba(255, 255, 255, 0.08)",
          divider: "rgba(255, 255, 255, 0.12)",
        },
        nancie: {
          main: nancieViolet,
          gradient: nancieGradient,
        },
        // Aliases kept for KPI format rules stored in tenant databases (backend-django/data/kpis.json).
        icon: {
          nancie: nancieViolet,
          recoPriceUp: greenInk,
          recoMinSet: greenInk,
        },
        secondary: {
          main: mint,
        },
        status: {
          pending: "#A3820A",
          processing: "#FF8D47",
          success: mint,
          stale: "#767676",
          awaitRefresh: "#0077B6",
        },
        success: {
          main: "#136f46",
        },
        info: {
          main: "#0b7f7d",
        },
        warning: {
          main: "#C45500",
        },
        text: {
          primary: "#1e1d1e",
          secondary: "#6d6a65",
          disabled: "rgba(0, 0, 0, 0.38)",
          cell: "#767676",
          // One step darker than primary.main: ink must hold 4.5:1 on the cream page
          // and the sunken grey cells, where the button green only reaches 4.3 / 4.0.
          accent: "#136f46",
          // Accent ink on inverted surfaces (filter bar, menu): M3 inversePrimary.
          accentInverted: mint,
          contrastPrimary: cream,
        },
        tooltip: {
          background: darkGreen,
        },
        shadow: {
          main: "0px 1px 3px rgba(0, 0, 0, 0.04), 0px 4px 12px rgba(0, 0, 0, 0.06)",
          highlightTileBar: "unset",
          highlightKpi: "unset",
          highlightLine: "0px 0px 2px rgba(226, 217, 192, 0.4)",
        },
        DataGrid: {
          bg: "#FFFFFF",
          headerBg: "#ebebeb",
        },
      },
    },
    dark: {
      palette: {
        mode: "dark",
        common: {
          white: white,
        },
        customColors: {
          purple: {
            main: "#8773b1",
          },
          orange: {
            main: "#FF8D47",
          },
        },
        background: {
          default: navy900,
          dialog: navy900,
          input: secondaryColor,
          login: "rgb(69 73 101 / 40%)",
          paper: navy700,
          inverted: navy800,
          sunken: navy800,
        },
        border: {
          // Cards draw their outline in light only (MuiPaper); kept for the palette shape.
          paper: "transparent",
          // Same as background.input: in dark a field is its fill, the outline
          // only bounds fields that have no fill of their own.
          inputOutlined: secondaryColor,
          dotted: "rgba(255, 255, 255, 0.24)",
        },
        button: {
          background: primaryColor,
          backgroundHover: "#00b7b8",
          text: "rgba(0, 0, 0, 0.87)",
          tonalBackground: "rgba(255, 255, 255, 0.12)",
          tonalBackgroundHover: "rgba(255, 255, 255, 0.2)",
          switch: "rgba(255, 255, 255, 0.35)",
          chip: "rgba(255, 255, 255, 0.16)",
          chipHover: "rgba(255, 255, 255, 0.26)",
          chipText: "rgba(255, 255, 255, 0.87)",
        },
        divider: "rgba(255, 255, 255, 0.1)",
        error: {
          main: "#ff585d",
          dark: "#EC5D59",
        },
        filter: {
          background: navy800,
          text: "rgba(255, 255, 255, 0.87)",
          textSecondary: "rgba(255, 255, 255, 0.54)",
          textDisabled: "rgba(255, 255, 255, 0.38)",
          tabBackground: "rgba(255, 255, 255, 0.06)",
          toggleBackground: "transparent",
          toggleBackgroundHover: "rgba(255, 255, 255, 0.08)",
          toggleSelectedBackground: secondaryColor,
          toggleSelectedBackgroundHover: "rgba(255, 255, 255, 0.24)",
          toggleBorder: secondaryColor,
          toggleSelectedText: cyan,
        },
        chart: {
          positive: cyan,
          negative: "#ff585d",
          neutral: "#b0b6bd",
          neutralDark: "#7d8289",
          neutralStroke: "#b0b6bd",
          previous: "#929292",
          forecast: "#c9cbd1",
          model: nancieViolet,
          pm: "#93b1e0",
          pickup: "#f69200",
          cancellation: "#ffab91",
          reference: "#f69200",
          raf: "#bcbdbd33",
          corridor: "#465072",
          salesWash: "#90e8e8",
          pickupWash: "#d4ba98",
          axis: "#bcbdbd",
          grid: "#3c4566",
          highlight: "rgba(37, 40, 63, 0.3)",
          selection: "rgba(188, 189, 189, 0.5)",
          needle: "#d6d6d6",
          event: "rgba(120, 60, 200, 0.6)",
          eventStroke: "rgba(140, 80, 220, 0.8)",
          eventText: "#e0d0ff",
          gauge: {
            target: "#00b0ac",
            empty: "#f2f2f2",
            edge: "#d9d9d9",
            deciles25: "#00cccc",
            decile6: "#57d1d1",
            decile7: "#6cd2d2",
            decile8: "#82d4d4",
            decile9: "#aed7d7",
          },
          series: {
            1: "#fc9b6f",
            2: "#7dbdfe",
            3: "#fd9696",
            4: "#a3b2fe",
            5: "#6fdea7",
            6: "#e3ad4b",
            7: "#ea97d8",
            8: "#b5c159",
            9: "#b191ea",
            10: "#89b559",
            11: "#4ae0ff",
            12: "#df8f48",
          },
        },
        LinearProgress: {
          track: "rgba(255, 255, 255, 0.18)",
        },
        primary: {
          main: primaryColor,
          light: cyan,
        },
        menu: {
          background: navy700,
          text: white,
          hover: "rgba(255, 255, 255, 0.08)",
          divider: "rgba(255, 255, 255, 0.12)",
        },
        nancie: {
          main: nancieViolet,
          gradient: nancieGradient,
        },
        icon: {
          nancie: nancieViolet,
          recoPriceUp: primaryColor,
          recoMinSet: primaryColor,
        },
        secondary: {
          main: secondaryColor,
        },
        status: {
          pending: "#FFEB3B",
          processing: "#FF8D47",
          success: "#1ed066",
          stale: "#AAACB4",
          awaitRefresh: "#64B5F6",
        },
        warning: {
          main: "#f39200",
        },
        success: {
          main: "#00D986",
        },
        info: {
          main: primaryColor,
        },
        text: {
          primary: "rgba(255, 255, 255, 0.87)",
          secondary: "rgba(255, 255, 255, 0.56)",
          disabled: "rgba(255, 255, 255, 0.38)",
          cell: "#a6a6a6",
          accent: cyan,
          accentInverted: cyan,
          contrastPrimary: "rgba(255, 255, 255, 0.87)",
        },
        tooltip: {
          background: "#626b80",
        },
        shadow: {
          main: "0px 2px 8px rgba(0, 0, 0, 0.15), 0px 8px 24px rgba(0, 0, 0, 0.1)",
          highlightTileBar: "0 0 10px white",
          highlightKpi: "0 0 20px white",
          highlightLine: "0px 0px 2px rgba(255, 255, 255, 0.6)",
        },
        DataGrid: {
          bg: navy700,
          headerBg: navy700,
        },
      },
    },
  },
  colorSchemeSelector: "class",
  defaultColorScheme: "dark",
});

export const SCROLL_BAR_STYLES = (theme: Theme) => ({
  "& *::-webkit-scrollbar-thumb": {
    border: `2px solid ${theme.vars.palette.background.sunken}`,
    borderRadius: "6px",
    backgroundColor: theme.vars.palette.action.disabled,
  },
  "& *::-webkit-scrollbar-corner, & *::-webkit-scrollbar": {
    backgroundColor: theme.vars.palette.background.sunken,
  },
});

/** The gutter a thin vertical scrollbar takes, for layouts that must allow for it. */
export const SCROLL_BAR_WIDTH = 10;

export const SCROLL_BAR_STYLES_THIN = {
  "&::-webkit-scrollbar": {
    width: `${SCROLL_BAR_WIDTH}px`,
  },
};

/**
 * Use hex color graph to retrieve it theme color equivalence
 *
 * This function has been created at the creation of the light theme
 * This allows us not to change one by one the colors that are sent to us by the back for the graphs
 * and get the right color according to the theme
 *
 * @param color
 */
export const graphColorConverter = (color: string, theme: Theme) => {
  const chart = theme.vars.palette.chart;
  const colorsEquivalence: Record<string, string> = {
    cyan: chart.positive,
    "#21f1f3": chart.positive,
    "#00cccc": chart.positive,
    "#8773b1": chart.previous,
    "#bcbdbd": chart.neutral,
    "#f69200": chart.reference,
    "#ff585d": chart.negative,
    "#ff4b54": chart.negative,
    "#60e684": chart.pm,
    "#9aa0a6": chart.forecast,
  };

  return colorsEquivalence[color.toLowerCase()] || color;
};

/** Chart dashed stroke rule: */

// Every N-1 series (curve, capacity, price ...).
export const N1_DASH = "4 3";
// The "N-1 final" variant drawn next to an "N-1 à date" of the same colour.
export const N1_FINAL_DASH = "1 4";
// Other reference curves (projection, scope backdrop, reco bridge, compared
// model): longer dash so they are never read as N-1.
export const REFERENCE_DASH = "8 4";

export type SeriesPool = "primary" | "secondary" | "all";

// Categorical series colour by index. Slots 1-6 are the primary pool (UH cats,
// segments), 7-12 the secondary pool (competitors): two series drawn from
// different pools on the same chart can never share a hue.
export const seriesColor = (
  theme: Theme,
  index: number,
  pool: SeriesPool = "all"
): string => {
  const series = theme.vars.palette.chart.series;
  const slot =
    pool === "primary"
      ? 1 + (index % 6)
      : pool === "secondary"
      ? 7 + (index % 6)
      : 1 + (index % 12);
  return series[slot as keyof typeof series] ?? series[1];
};
